import { z } from 'zod'
import { membershipSchema } from './membership.ts'

/**
 * The desktop app's own surface. It has no session and no user: the app
 * registers itself on first launch and authenticates with a bearer token from
 * then on.
 *
 * There is no account recovery. A lost token is a lost balance, which is why
 * `token` below is returned exactly once and never again.
 */
export const devicePlatformSchema = z.enum(['win32', 'darwin', 'linux'])

export type DevicePlatform = z.infer<typeof devicePlatformSchema>

export const registerDeviceRequestSchema = z.object({
  platform: devicePlatformSchema,
  appVersion: z.string().trim().min(1).max(32),
})

export type RegisterDeviceRequest = z.infer<typeof registerDeviceRequestSchema>

export const registerDeviceResponseSchema = z.object({
  /** Returned once, at registration. Never retrievable afterwards. */
  token: z.string(),
  /** Safe to display and to quote to support. Also the token's first segment. */
  accountId: z.string(),
  createdAt: z.string(),
})

export type RegisterDeviceResponse = z.infer<typeof registerDeviceResponseSchema>

export const deviceAccountSchema = z.object({
  accountId: z.string(),
  balanceUsd: z.number(),
  toppedUpUsd: z.number(),
  spentUsd: z.number(),
  balanceIdr: z.number(),
  usdIdrRate: z.number(),
  /** False means the app still has to call `POST /keys` before it can infer. */
  hasApiKey: z.boolean(),
  membership: membershipSchema,
  blocked: z.boolean(),
  blockedReason: z.string().nullable(),
})

export type DeviceAccount = z.infer<typeof deviceAccountSchema>

/**
 * The version gate. Unauthenticated, because an app too old to authenticate
 * still has to be able to learn that it is too old.
 *
 * Comparison is the client's job and must be numeric per segment — `"2.10.0"`
 * sorts before `"2.9.0"` as a string.
 */
export const appInfoSchema = z.object({
  minSupportedVersion: z.string(),
  latestVersion: z.string(),
  downloadUrl: z.string(),
  /** Release notes for `latestVersion`, newline separated. */
  changelog: z.string(),
})

export type AppInfo = z.infer<typeof appInfoSchema>

/**
 * The banner the app shows at launch — what n8n served as
 * `/webhook/yt-clipper/notification`.
 *
 * `id` is new: without one the app can only decide "have I seen this" by
 * comparing the message text, so an edited typo re-nags everybody. It changes
 * whenever the content does.
 */
export const appNotificationSchema = z.object({
  id: z.string(),
  message: z.string(),
  image: z.string().nullable(),
  link: z.string().nullable(),
  linkText: z.string().nullable(),
})

export type AppNotification = z.infer<typeof appNotificationSchema>

/** Null rather than an empty object, so "nothing to show" is unambiguous. */
export const appNotificationResponseSchema = z.object({
  notification: appNotificationSchema.nullable(),
})

export const appMenuItemSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** A lucide icon name, resolved by the app. */
  icon: z.string(),
  url: z.string(),
})

export type AppMenuItem = z.infer<typeof appMenuItemSchema>

export const appMenuSchema = z.object({ items: z.array(appMenuItemSchema) })

export type AppMenu = z.infer<typeof appMenuSchema>
